import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define types
export interface CartItem {
  id: string;
  title: string;
  artist: string;
  url: string;
  coverArt?: string;
  year?: string;
  genre?: string[];
  price?: number;
  isAcetate?: boolean;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  cartCount: number;
}

const defaultContext: CartContextType = {
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
  isInCart: () => false,
  cartCount: 0,
};

const CartContext = createContext<CartContextType>(defaultContext);

// Provider component
export const CartProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartCount, setCartCount] = useState(0);
  
  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('vintageMusicCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
        setCartCount(parsedCart.length);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
      }
    }
  }, []);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vintageMusicCart', JSON.stringify(cartItems));
    setCartCount(cartItems.length);
  }, [cartItems]);
  
  // Add item to cart
  const addToCart = (item: CartItem) => {
    // Check if item is already in cart
    if (!cartItems.some(cartItem => cartItem.id === item.id)) {
      setCartItems(prev => [...prev, item]);
    }
  };
  
  // Remove item from cart
  const removeFromCart = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };
  
  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };
  
  // Check if item is in cart
  const isInCart = (id: string) => {
    return cartItems.some(item => item.id === id);
  };
  
  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart,
    cartCount,
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;